import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      // Update active section based on scroll position
      const sections = ['home', 'about', 'vpn', 'support', 'contact'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="https://public.readdy.ai/ai/img_res/7f0e5952-9675-4a23-851d-f01f1b780378.png" 
                alt="TRANSPORTI d.o.o." 
                className="h-12 w-auto"
              />
              <span className={`text-xl font-bold transition-colors ${
                isScrolled ? 'text-gray-900' : 'text-white'
              }`}>TRANSPORTI</span>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              {['home', 'about', 'vpn', 'support', 'contact'].map((section) => (
                <button
                  key={section}
                  onClick={() => scrollToSection(section)}
                  className={`text-sm font-medium transition-colors capitalize cursor-pointer whitespace-nowrap ${
                    activeSection === section
                      ? isScrolled ? 'text-teal-600' : 'text-white'
                      : isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white/90 hover:text-white'
                  }`}
                >
                  {section}
                </button>
              ))}
            </div>

            <button 
              onClick={() => scrollToSection('contact')}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                isScrolled 
                  ? 'bg-teal-600 text-white hover:bg-teal-700' 
                  : 'bg-white text-gray-900 hover:bg-gray-100'
              }`}
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-purple-50 to-indigo-900"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/30"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="grid md:grid-cols-5 gap-12 items-center">
            <div className="md:col-span-3">
              <div className="inline-flex items-center space-x-2 bg-white/95 backdrop-blur-sm px-5 py-2.5 rounded-full mb-8">
                <i className="ri-shield-check-line text-teal-600 text-lg"></i>
                <span className="text-sm font-medium text-gray-700">Enterprise VPN Solution</span>
              </div>
              
              <h1 className="text-6xl md:text-7xl font-extrabold text-white mb-8 leading-tight">
                Secure Your Digital Freedom
              </h1>
              
              <p className="text-lg text-white/90 mb-10 leading-relaxed max-w-xl">
                Experience unparalleled online privacy and security with TRANSPORTI VPN. Military-grade encryption protects your data while you browse freely across the globe.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="px-8 py-4 bg-black text-white rounded-2xl font-medium hover:bg-gray-900 transition-all cursor-pointer whitespace-nowrap"
                >
                  Get Started
                </button>
                <button 
                  onClick={() => scrollToSection('vpn')}
                  className="px-8 py-4 text-white font-medium hover:underline transition-all cursor-pointer whitespace-nowrap"
                >
                  Learn More
                </button>
              </div>
            </div>
          </div>
          
          <div className="mt-20">
            <div className="w-full h-80 rounded-3xl overflow-hidden">
              <img 
                src="https://public.readdy.ai/ai/img_res/ed1dbbb2e82e9ec7d55f2490e74efe51.jpg"
                alt="Network Security"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-32 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-start mb-16">
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight max-w-xl mb-6 md:mb-0" style={{ fontFamily: 'Playfair Display, serif' }}>
              Why Choose TRANSPORTI
            </h2>
            <p className="text-base text-gray-600 max-w-md leading-relaxed">
              Our VPN solution combines cutting-edge technology with user-friendly design to deliver the ultimate privacy experience.
            </p>
          </div>

          <div className="grid md:grid-cols-10 gap-8">
            <div className="md:col-span-4 group cursor-pointer">
              <div className="relative h-full min-h-[500px] bg-gradient-to-br from-slate-900 to-indigo-900 rounded-3xl overflow-hidden transition-all duration-500 hover:scale-105">
                <div className="absolute inset-0 opacity-20">
                  <img 
                    src="https://readdy.ai/api/search-image?query=abstract%20geometric%20pattern%20with%20hexagonal%20shapes%20and%20circuit%20board%20elements%2C%20dark%20navy%20blue%20background%20with%20subtle%20glowing%20lines%2C%20modern%20tech%20encryption%20visualization%2C%20minimalist%20digital%20security%20concept&width=600&height=800&seq=encryption-pattern&orientation=portrait"
                    alt="Encryption"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-3xl font-bold text-white mb-3">Military-Grade Encryption</h3>
                  <p className="text-white/80 text-base leading-relaxed">AES-256 encryption protects your data from any potential threats</p>
                </div>
              </div>
            </div>

            <div className="md:col-span-3">
              <div className="relative h-full min-h-[500px] bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl overflow-hidden transition-all duration-300 hover:shadow-xl">
                <div className="absolute inset-0 flex items-center justify-center">
                  <img 
                    src="https://readdy.ai/api/search-image?query=minimalist%20world%20map%20with%20glowing%20connection%20points%20and%20network%20lines%2C%20light%20gray%20background%20with%20blue%20accent%20points%2C%20clean%20modern%20global%20connectivity%20visualization%2C%20simple%20elegant%20tech%20illustration&width=500&height=800&seq=global-network&orientation=portrait"
                    alt="Global Network"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-transparent to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-3xl font-bold text-white mb-3">Global Server Network</h3>
                  <p className="text-white/90 text-base leading-relaxed">Connect to servers worldwide for optimal speed</p>
                </div>
              </div>
            </div>

            <div className="md:col-span-3">
              <div className="relative h-full min-h-[500px] bg-gradient-to-br from-cyan-500 to-blue-600 rounded-3xl overflow-hidden transition-all duration-300 hover:shadow-xl">
                <div className="absolute inset-0 opacity-30">
                  <img 
                    src="https://readdy.ai/api/search-image?query=abstract%20privacy%20shield%20icon%20with%20lock%20symbol%2C%20vibrant%20cyan%20and%20blue%20gradient%20background%2C%20modern%20digital%20security%20concept%20with%20clean%20lines%2C%20minimalist%20tech%20illustration%20with%20glowing%20effects&width=500&height=800&seq=privacy-shield&orientation=portrait"
                    alt="Privacy"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/70 via-transparent to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-3xl font-bold text-white mb-3">Zero-Log Policy</h3>
                  <p className="text-white/90 text-base leading-relaxed">Complete privacy guaranteed with no activity tracking</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12">
            <span className="text-xs uppercase tracking-wider text-gray-500 font-medium">About TRANSPORTI</span>
          </div>

          <div className="grid md:grid-cols-3 gap-16 items-start">
            <div className="md:col-span-2">
              <h2 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight mb-12" style={{ fontFamily: 'Playfair Display, serif' }}>
                Building Trust Through Technology
              </h2>
              
              <p className="text-lg text-gray-600 leading-relaxed max-w-2xl">
                TRANSPORTI d.o.o. is a Croatian technology company dedicated to developing innovative applications that enhance digital security and privacy. Based in Rijeka, we specialize in creating robust VPN solutions that empower users to browse the internet safely and freely. Our commitment to excellence and user privacy drives everything we do, from development to customer support.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-5 gap-6 mt-20">
            <div className="md:col-span-2 h-80 w-full">
              <img 
                src="https://readdy.ai/api/search-image?query=modern%20minimalist%20office%20workspace%20with%20computers%20and%20technology%20equipment%2C%20bright%20natural%20lighting%2C%20clean%20professional%20environment%2C%20contemporary%20business%20setting%20with%20neutral%20tones%20and%20organized%20desk%20setup&width=700&height=500&seq=office-workspace&orientation=landscape"
                alt="TRANSPORTI Office"
                className="w-full h-full object-cover object-top rounded-3xl"
              />
            </div>
            <div className="md:col-span-3 h-80 w-full">
              <img 
                src="https://readdy.ai/api/search-image?query=abstract%20technology%20infrastructure%20visualization%20with%20server%20racks%20and%20network%20cables%2C%20blue%20and%20purple%20lighting%2C%20modern%20data%20center%20concept%2C%20clean%20professional%20tech%20environment%20with%20organized%20equipment%20and%20glowing%20indicators&width=900&height=500&seq=tech-infrastructure&orientation=landscape"
                alt="Technology Infrastructure"
                className="w-full h-full object-cover object-top rounded-3xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* VPN Section */}
      <section id="vpn" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              TRANSPORTI VPN Features
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Experience the next generation of online privacy with our comprehensive VPN solution
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-gray-50 to-white p-10 rounded-3xl border border-gray-100 hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-lock-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Advanced Security</h3>
              <p className="text-gray-600 leading-relaxed">
                Industry-leading AES-256 encryption ensures your data remains completely secure and private at all times.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white p-10 rounded-3xl border border-gray-100 hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-speed-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Lightning Fast</h3>
              <p className="text-gray-600 leading-relaxed">
                Optimized servers worldwide deliver blazing-fast speeds without compromising your security or privacy.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white p-10 rounded-3xl border border-gray-100 hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-global-line text-3xl text-purple-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Global Access</h3>
              <p className="text-gray-600 leading-relaxed">
                Access content from anywhere in the world with our extensive network of high-performance servers.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white p-10 rounded-3xl border border-gray-100 hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">No-Log Policy</h3>
              <p className="text-gray-600 leading-relaxed">
                We never track, collect, or store your online activity. Your privacy is our top priority.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white p-10 rounded-3xl border border-gray-100 hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-device-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Multi-Device Support</h3>
              <p className="text-gray-600 leading-relaxed">
                Protect all your devices with a single account. Compatible with Windows, Mac, iOS, and Android.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white p-10 rounded-3xl border border-gray-100 hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-customer-service-line text-3xl text-red-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">24/7 Support</h3>
              <p className="text-gray-600 leading-relaxed">
                Our dedicated support team is always available to help you with any questions or concerns.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-40 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=abstract%20digital%20security%20concept%20with%20flowing%20blue%20and%20purple%20gradient%2C%20futuristic%20cybersecurity%20visualization%20with%20glowing%20particles%20and%20network%20connections%2C%20modern%20tech%20background%20with%20depth%20and%20dimension%2C%20cinematic%20lighting&width=1920&height=800&seq=cta-background&orientation=landscape"
            alt="Get Started"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/80 via-purple-900/70 to-blue-900/80"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end">
            <div>
              <h2 className="text-6xl md:text-7xl font-extrabold text-white mb-12 leading-tight lowercase">
                ready to get started?
              </h2>
              
              <button 
                onClick={() => scrollToSection('contact')}
                className="group inline-flex items-center bg-white rounded-full pl-8 pr-3 py-3 hover:pr-6 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <span className="text-black font-semibold text-lg mr-4">Start Free Trial</span>
                <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                  <i className="ri-arrow-right-line text-white text-xl"></i>
                </div>
              </button>
            </div>

            <div className="mt-12 md:mt-0">
              <p className="text-white/90 text-xl leading-relaxed text-right font-light">
                No credit card required<br />
                Cancel anytime
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section id="support" className="py-32 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              How Can We Help?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Find answers to common questions or reach out to our support team
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-white p-8 rounded-3xl border border-gray-200 hover:shadow-lg transition-all">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-question-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">How do I install TRANSPORTI VPN?</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Download the app for your device, create an account, and connect with a single click. Our setup wizard guides you through the entire process.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-200 hover:shadow-lg transition-all">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-device-line text-2xl text-blue-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Which devices are supported?</h3>
                  <p className="text-gray-600 leading-relaxed">
                    TRANSPORTI VPN works on Windows, macOS, iOS, Android, and Linux. You can protect up to 5 devices simultaneously with one account.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-200 hover:shadow-lg transition-all">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-2xl text-purple-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Is my data really private?</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Absolutely. We maintain a strict no-log policy and use military-grade encryption. We never track, collect, or share your online activity.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-200 hover:shadow-lg transition-all">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-customer-service-line text-2xl text-green-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">How do I contact support?</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Our support team is available 24/7 via email at support@transportidoo.com or by phone at +1 855 408 1264.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-start mb-16">
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 md:mb-0" style={{ fontFamily: 'Playfair Display, serif' }}>
              Get in Touch
            </h2>
            <p className="text-base text-gray-600 max-w-md leading-relaxed">
              We're here to help with any questions about TRANSPORTI VPN
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-10 rounded-3xl hover:shadow-lg transition-all">
              <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-mail-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Email Support</h3>
              <a href="mailto:support@transportidoo.com" className="text-base text-gray-700 hover:text-teal-600 transition-colors">
                support@transportidoo.com
              </a>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-10 rounded-3xl hover:shadow-lg transition-all">
              <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-phone-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Call Us</h3>
              <a href="tel:+18554081264" className="text-base text-gray-700 hover:text-blue-600 transition-colors">
                +1 855 408 1264
              </a>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-10 rounded-3xl hover:shadow-lg transition-all">
              <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-map-pin-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Visit Office</h3>
              <address className="text-base text-gray-700 not-italic leading-relaxed">
                Zagrebacka 16<br />
                51000 Rijeka<br />
                Croatia
              </address>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-emerald-900 to-teal-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-16">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <img 
                  src="https://public.readdy.ai/ai/img_res/7f0e5952-9675-4a23-851d-f01f1b780378.png" 
                  alt="TRANSPORTI d.o.o." 
                  className="h-10 w-auto brightness-0 invert"
                />
                <span className="text-xl font-bold">TRANSPORTI</span>
              </div>
              <p className="text-sm text-white/70 leading-relaxed">
                Secure VPN solutions for digital freedom and privacy protection worldwide.
              </p>
            </div>

            <div>
              <h4 className="text-xs uppercase tracking-wider text-white/60 font-semibold mb-6">NEWSLETTER</h4>
              <div className="flex items-center border-b border-white/30 pb-2 mb-4">
                <input 
                  type="email" 
                  placeholder="Your email"
                  className="bg-transparent text-white placeholder-white/50 text-sm flex-1 outline-none"
                />
                <button className="text-white hover:text-white/80 transition-colors cursor-pointer">
                  <i className="ri-arrow-right-line text-lg"></i>
                </button>
              </div>
              <a href="#" className="text-xs text-white/60 hover:text-white/80 underline transition-colors">
                Privacy Policy
              </a>
            </div>

            <div>
              <h4 className="text-xs uppercase tracking-wider text-white/60 font-semibold mb-6">PRODUCT</h4>
              <ul className="space-y-4">
                <li>
                  <button onClick={() => scrollToSection('vpn')} className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer">
                    VPN Features
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('vpn')} className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer">
                    Security
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('support')} className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer">
                    Support
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs uppercase tracking-wider text-white/60 font-semibold mb-6">COMPANY</h4>
              <ul className="space-y-4">
                <li>
                  <button onClick={() => scrollToSection('about')} className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer">
                    About
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('support')} className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer">
                    Support
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('contact')} className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer">
                    Contact
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/20 pt-12">
            <div className="text-center mb-8">
              <h2 className="text-8xl md:text-9xl font-bold text-white/10 tracking-tight" style={{ fontFamily: 'Arial Black, sans-serif' }}>
                TRANSPORTI
              </h2>
            </div>
            <div className="flex flex-col md:flex-row justify-between items-center text-sm text-white/60">
              <p>&copy; 2025 TRANSPORTI d.o.o. All rights reserved.</p>
              <a 
                href="https://readdy.ai/?origin=logo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-white/80 transition-colors mt-4 md:mt-0"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
